<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Production Template
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-card class="pa-4">
                  <div class="d-flex justify-space-between mb-4">
                    <h2 class="text-h6 font-weight-bold">Template List</h2>

                    <v-btn
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi mdi-plus"
                      rounded="xl"
                      @click="openCreate"
                    >
                      New Template
                    </v-btn>
                  </div>

                  <v-data-table
                    :headers="headers"
                    :items="templateList"
                    :item-value="'id'"
                    class="elevation-1"
                  >
                    <template #item.index="{ index }">
                      {{ ++index }}
                    </template>

                    <template #item.status="{ item }">
                      <v-chip
                        v-if="item.status == 1"
                        color="success"
                        size="small"
                      >
                        Active
                      </v-chip>

                      <v-chip v-else color="red" size="small">
                        Inactive
                      </v-chip>
                    </template>

                    <template v-slot:item.actions="{ item }">

                      <v-icon
                          small
                          @click="viewDetails(item)"
                          
                          class="me-2 edit-button"
                          title="View Invoice"
                          color="blue"
                      >
                        mdi-file-document-outline
                    </v-icon>

                    <v-icon
                      class="me-2 edit-button"
                      color="primary"
                      @click="editTemplate(item)"
                      
                    >
                      mdi mdi-pencil-outline
                    </v-icon>
                    </template>
                  </v-data-table>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- MODAL -->

      <v-dialog v-model="dialog" max-width="800">
        <v-card>
          <v-card-title>
            {{ isEdit ? "Update Template" : "Create Template" }}
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="name"
                  label="Template Name *"
                  variant="outlined"
                  density="compact"
                  clearable
                  :error-messages="nameError"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete
                  label="Product*"
                  v-model="product_id"
                  :items="getProductMapList"
                  :item-title="(item) => `${item.product.code}-${item.product.name}` ?? 'N/A'" 
                  item-value="product_id"
                  density="compact"
                  variant="outlined"
                  clearable
                  :error-messages="productError"
                />
              </v-col>

              <v-col cols="12">
                <h4 class="mb-2">Raw Products</h4>

                <v-row v-for="(item,index) in cart" :key="index">
                  <v-col cols="4">
                    <v-autocomplete
                        label="Raw Product*"
                        v-model="item.raw_product_id"
                        :items="getProductMapList"
                        :item-title="(item) => `${item.product.code}-${item.product.name}` ?? 'N/A'" 
                        item-value="product_id"
                        density="compact"
                        variant="outlined"
                        clearable
                        :error-messages="errors?.cart?.[index]?.raw_product_id"
                        />
                  </v-col>

                  <v-col cols="3">
                    <v-text-field
                        type="number"
                        v-model="item.quantity"
                        label="Quantity *"
                        variant="outlined"
                        density="compact"
                        clearable
                        :error-messages="errors?.cart?.[index]?.quantity"
                        />
                  </v-col>

                  <v-col cols="3">
                        <v-text-field
                            type="number"
                            v-model="item.process_loss_percent"
                            label="Loss %*"
                            variant="outlined"
                            density="compact"
                            clearable
                            :error-messages="errors?.cart?.[index]?.process_loss_percent"
                        />
                  </v-col>

                  <v-col cols="2" class="d-flex align-center">
                    <v-btn
                      v-if="(index != cart.length - 1) || (cart.length > 1)"
                      icon
                      variant="tonal"
                      size="small"
                      color="primary"
                      class="pa-1 breakdown-input mb-2"
                      @click="removeRow(index)"
                    >
                      <v-icon color="red">mdi-close</v-icon>
                    </v-btn>

                    <v-btn
                      v-if="index == cart.length - 1"
                      variant="tonal"
                      icon
                      size="small"
                      color="primary"
                      class="breakdown-input mb-2"
                      @click="addRow"
                    >
                      <v-icon color="blue">mdi-plus</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  label="Active"
                  v-model="status"
                  :true-value="1"
                  :false-value="0"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn class="bg-red" @click="dialog=false"> Close </v-btn>
              <v-btn class="bg-brand w-15 ms-2" @click="saveTemplate"> Save</v-btn>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script setup>

import { ref,computed,onMounted } from "vue"
import { useStore } from "vuex"

import { useForm,useField } from "vee-validate"
import * as yup from "yup"
import { useRouter } from "vue-router";
const router = useRouter();



const store = useStore()



const panel = ref([0])
const dialog = ref(false)
const isEdit = ref(false)

const templateId = ref(null)



/* -----------------------------
YUP VALIDATION
--------------------------------*/

const schema = yup.object({

  name: yup
    .string()
    .required("Template name is required"),

  product_id: yup
    .number()
    .required("Product is required")
    .typeError("Product is required"),

  status: yup.number(),

//   cart: yup
//     .array()
//     .of(
//       yup.object({
//         raw_product_id: yup
//           .number()
//           .required("Raw product is required")
//           .typeError("Raw product is required"),

//         quantity: yup
//           .number()
//           .required("Quantity required")
//           .min(0.01, "Quantity must be greater than 0"),

//         process_loss_percent: yup
//           .number()
//           .required("Loss percent required")
//           .min(0, "Loss cannot be negative")
//           .max(100, "Loss cannot exceed 100%"),
//       })
//     )
//     .min(1, "At least one raw product required"),
});



const { handleSubmit,setErrors, resetForm  } = useForm({
  validationSchema: schema,
  initialValues: {
    name: "",
    product_id: null,
    status: 1,
    cart: []
  }
});



const { value:name,errorMessage:nameError } = useField("name")
const { value:product_id,errorMessage:productError } = useField("product_id")
const { value:status } = useField("status")



/* -----------------------------
CART
--------------------------------*/

const cart = ref([
{
raw_product_id:null,
quantity:0,
process_loss_percent:0
}
])



/* -----------------------------
TABLE
--------------------------------*/

const headers = [

{ title:"#SL", key:"index"},
{ title:"Code", key:"code"},
{ title:"Name", key:"name"},
{ title:"Product", key:"product.name"},
{ title:"Status", key:"status"},
{ title:"Actions", key:"actions"}

]



const templateList = computed(()=>{
return store.getters.getTemplateList
})


const getProductMapList = computed(()=>{
return store.getters.getProductMapList
})



/* -----------------------------
LOAD DATA
--------------------------------*/

const loadTemplates = async ()=>{
  store.commit("SET_LOADING", { color: "primary" });
  await store.dispatch("getTemplateList")
  store.commit("UNSET_LOADING");
}

const loadProduct = async ()=>{
  store.commit("SET_LOADING", { color: "primary" });
  await store.dispatch("getProductMapList")
  store.commit("UNSET_LOADING");
}



/* -----------------------------
CREATE
--------------------------------*/

const openCreate = ()=>{

isEdit.value=false

resetForm({
values:{
  name:"",
  product_id:null,
  status:1
}
})

cart.value=[{
  raw_product_id:null,
  quantity:0,
  process_loss_percent:0
}]

dialog.value=true

}



/* -----------------------------
EDIT
--------------------------------*/

const viewDetails = async (item) => {
  router.push({ name: "production-template-view", params: { id: item.id } });
};

const editTemplate = async(item)=>{

isEdit.value=true
templateId.value=item.id

const response = await store.dispatch("getTemplateById",item.id)

const data = response.data

name.value = data.name
product_id.value = data.product_id
status.value = data.status

cart.value = []

data.items.forEach((item)=>{
  cart.value.push({
    raw_product_id:item.raw_product_id,
    quantity:item.quantity,
    process_loss_percent:item.process_loss_percent
  })
})

dialog.value=true

}



/* -----------------------------
CART ROW
--------------------------------*/

const addRow = ()=>{

cart.value.push({
raw_product_id:null,
quantity:0,
process_loss_percent:0
})

}

const removeRow = (index)=>{
cart.value.splice(index,1)
}



/* -----------------------------
BUILD PAYLOAD
--------------------------------*/

const buildPayload = ()=>{

let payload={
name:name.value,
product_id:product_id.value,
status:status.value,
cart:[]
}

cart.value.forEach((item)=>{

payload.cart.push({
raw_product_id:item.raw_product_id,
quantity:item.quantity,
process_loss_percent:item.process_loss_percent
})

})

return payload

}



/* -----------------------------
SAVE
--------------------------------*/
const saveTemplate = handleSubmit(async (values)=>{

  values.cart = cart.value

  try{

    const payload = buildPayload()

    if(isEdit.value){

      payload.id = templateId.value
      await store.dispatch("updateTemplate",payload)

    }else{

      await store.dispatch("saveTemplate",payload)

    }

    dialog.value=false
    loadTemplates()

  }catch(error){

    if(error.response && error.response.status === 422){

      const backendErrors = error.response.data.errors
      setErrors(backendErrors)

    }

  }

})



/* -----------------------------
MOUNT
--------------------------------*/

onMounted(()=>{
  loadProduct()
  loadTemplates()
})
</script>
